import React, { memo, useCallback, useMemo, useState } from 'react';

import { Modal, Table } from '@components/common';

import { ISortBy, ITablePagination, ITableSort } from '@interfaces/components/table';
import { IExportFileDTO } from '@interfaces/platform/dto/platformDashboardDTO';
import { IAssetTypesOrganizationDashboard, IDashboardAssetTypeSummary } from '@interfaces/platform/platformDashboard';

import DashboardPOAssetTypePerOrganizationTable from '../DashboardPOAssetTypePerOrganizationTable';
import DashboardPOWhiteCard from '../DashboardPOWhiteCard';
import { getAssetTypesColumns } from './utils';

const MODAL_WIDTH = '624px';
const MODAL_HEIGHT = '720px';
const TABLE_HEIGHT = '675px';

interface IDashboardPOAssetTypesTable {
	dashboardDashboardAssetTypesSummary: IDashboardAssetTypeSummary[] | IAssetTypesOrganizationDashboard[];
	isLoadingDashboardAssetTypesSummary: boolean;
	sortFieldName: string | null;
	sortDirection: boolean | null;
	handleSort: (sortBy: ISortBy[]) => void;
	handleExport: (params: IExportFileDTO) => void;
	isSingleOrganization?: boolean;
	pagination?: ITablePagination;
	wrapperClassName?: string;
}

const DashboardPOAssetTypesTable = ({
	dashboardDashboardAssetTypesSummary,
	isLoadingDashboardAssetTypesSummary,
	sortFieldName,
	sortDirection,
	handleSort,
	handleExport,
	isSingleOrganization,
	wrapperClassName,
	pagination,
}: IDashboardPOAssetTypesTable) => {
	const [selectedAsset, setSelectedAsset] = useState<IDashboardAssetTypeSummary | null>(null);

	const sort: ITableSort = useMemo(() => ({
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	}), [sortFieldName, sortDirection, handleSort]);

	const openOrganizationsTable = useCallback((asset?: IDashboardAssetTypeSummary) => {
		if (!asset) return null;

		setSelectedAsset(asset);
	}, []);

	const closeStatusTable = useCallback(() => setSelectedAsset(null), []);

	const columns = useMemo(
		() => getAssetTypesColumns({ openOrganizationsTable, isSingleOrganization })
		, [openOrganizationsTable, isSingleOrganization]);

	return (
		<DashboardPOWhiteCard
			className={wrapperClassName}
			handleExport={handleExport}
			isWithExport
			title='Assets by Asset type'
		>
			<Table
				columns={columns}
				data={dashboardDashboardAssetTypesSummary}
				isLoading={isLoadingDashboardAssetTypesSummary}
				isWithoutActions
				isWithoutCheckbox
				isWithoutDeleteAction
				isWithoutFilter
				maxHeight={TABLE_HEIGHT}
				pagination={pagination}
				sort={sort}
			/>
			{selectedAsset && (
				<Modal
					height={MODAL_HEIGHT}
					isOpen={!!selectedAsset}
					isScrollableContent
					onClose={closeStatusTable}
					title='List per Organisation'
					width={MODAL_WIDTH}
				>
					<DashboardPOAssetTypePerOrganizationTable
						assetIconId={selectedAsset?.type.icon.id}
						assetSubTypeId={selectedAsset?.subType?.id}
						assetTypeId={selectedAsset?.type.id}
						assetTypeName={selectedAsset?.type.assetTypeName}
					/>
				</Modal>
			)}
		</DashboardPOWhiteCard>
	);
};

export default memo(DashboardPOAssetTypesTable);
